import { useEffect, useState } from 'react';
import { Storage } from '../../lib/storage';
import { loadDeploymentConfig, type DeploymentConfig } from '../../lib/api/deploymentConfig';
import { fetchMe, type MeResult } from '../../lib/api/me';
import { playerSurfaceActive } from '../../lib/api/customerContext';
import { clubSession } from '../../lib/auth/instance';

// ─────────────────────────────────────────────────────────────────────
// WHAT THE BUG BUTTON MAY OFFER THIS PERSON (owner decision, Jens, 2026-10-22).
//
//   isAdmin    a bug administrator: the counts, the archive, and the form.
//              From `/me` (`bugAdmin`), which matches the staff account's
//              address against the deployment's allowlist.
//   canReport  the form, and nothing else: an administrator, or anybody
//              signed in where `/config` says filing is open.
//
// The server decides both and re-decides on every call; this only decides
// what is OFFERED, so nobody is shown a control every route would refuse.
//
// ── UNTIL IT KNOWS, NOTHING ──────────────────────────────────────
//
// On the http backend both start false and become true when the answers
// arrive. A button that flashed counts at somebody who may not read them, and
// then vanished, would be the worse failure.
//
// ── OFF THE HTTP BACKEND, THE OLD BEHAVIOUR ──────────────────────
//
// The local memory store has no server to separate reporters from
// administrators — the report and the archive are the same local list — so
// everybody has both, exactly as before.
//
// ── A PLAYER IS NEVER AN ADMINISTRATOR ───────────────────────────
//
// On the player surface (a member session, or an account acting as a player)
// `/me` is not asked: administration is the STAFF surface's, and the player
// surface's requests go to the member door. A player may report where filing
// is open.
// ─────────────────────────────────────────────────────────────────────

export interface BugAccess {
  canReport: boolean;
  isAdmin: boolean;
}

export interface BugAccessDeps {
  /** Is there a server to ask? Defaults to the bugs client's own test. */
  serverBacked?: () => boolean;
  memberMode?: () => boolean;
  loadConfig?: () => Promise<DeploymentConfig>;
  loadMe?: () => Promise<MeResult>;
}

const LOCAL: BugAccess = { canReport: true, isAdmin: true };
const NOTHING: BugAccess = { canReport: false, isAdmin: false };

/** Pure: what the two answers allow. */
export function bugAccessFrom(
  config: Pick<DeploymentConfig, 'bugReports'> | null,
  me: MeResult | null,
): BugAccess {
  const isAdmin = me?.state === 'ok' && me.me.bugAdmin === true;
  return { isAdmin, canReport: isAdmin || config?.bugReports.open === true };
}

export function useBugAccess(deps: BugAccessDeps = {}): BugAccess {
  const serverBacked = deps.serverBacked ?? (() => Storage.backend === 'http');
  const memberMode = deps.memberMode ?? playerSurfaceActive;
  const loadConfig = deps.loadConfig ?? loadDeploymentConfig;
  const loadMe = deps.loadMe ?? fetchMe;

  const local = !serverBacked();
  const [access, setAccess] = useState<BugAccess>(local ? LOCAL : NOTHING);

  // RE-ASKED WHEN THE SIGNED-IN ACCOUNT CHANGES — a persona switch or a
  // sign-in without a reload must not keep the previous person's answer.
  const [account, setAccount] = useState<string | null>(
    () => clubSession.state().account?.id ?? null,
  );
  useEffect(() => clubSession.subscribe((s) => setAccount(s.account?.id ?? null)), []);

  useEffect(() => {
    if (local) {
      setAccess(LOCAL);
      return undefined;
    }
    let live = true;
    setAccess(NOTHING);
    const member = memberMode();
    void Promise.all([
      loadConfig().catch(() => null),
      member ? Promise.resolve(null) : loadMe().catch(() => null),
    ]).then(([config, me]) => {
      if (live) setAccess(bugAccessFrom(config, me));
    });
    return () => {
      live = false;
    };
    // The loaders are stable module functions or test fakes; the account is
    // what the answer depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local, account]);

  return access;
}
